import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  RefreshCw, Calendar, Euro, AlertCircle, CheckCircle, 
  XCircle, Clock, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SubscriptionsManager = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/subscriptions/list`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubscriptions(data.subscriptions || []);
      } else {
        toast.error('Erreur lors du chargement des abonnements');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Impossible de charger les abonnements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const handleCancel = async (subscriptionId, immediate = false) => {
    const confirmMessage = immediate 
      ? 'Annuler cet abonnement immédiatement ? Le client perdra l\'accès tout de suite.'
      : 'Annuler cet abonnement à la fin de la période en cours ?';
    
    if (!window.confirm(confirmMessage)) return;

    setActionLoading(subscriptionId);
    try {
      const response = await fetch(
        `${API_URL}/api/subscriptions/${subscriptionId}/cancel?immediate=${immediate}`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );

      if (response.ok) {
        toast.success(immediate ? 'Abonnement annulé immédiatement' : 'Abonnement annulé à la fin de la période');
        fetchSubscriptions();
      } else {
        toast.error('Erreur lors de l\'annulation');
      }
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error('Erreur lors de l\'annulation');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivate = async (subscriptionId) => {
    setActionLoading(subscriptionId);
    try {
      const response = await fetch(
        `${API_URL}/api/subscriptions/${subscriptionId}/reactivate`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );

      if (response.ok) {
        toast.success('Abonnement réactivé');
        fetchSubscriptions();
      } else {
        toast.error('Erreur lors de la réactivation');
      }
    } catch (error) {
      console.error('Reactivate error:', error);
      toast.error('Erreur lors de la réactivation');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status, cancelAtPeriodEnd) => {
    if (cancelAtPeriodEnd) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-yellow-500/20 text-yellow-400 border border-yellow-500/50">
          <Clock className="w-3 h-3" />
          Annulation programmée
        </span>
      );
    }

    const statusConfig = {
      active: {
        label: 'Actif',
        icon: CheckCircle,
        className: 'bg-neon/20 text-neon border-neon/50'
      },
      trialing: {
        label: 'Essai',
        icon: Clock,
        className: 'bg-blue-500/20 text-blue-400 border-blue-500/50'
      },
      past_due: {
        label: 'Impayé',
        icon: AlertCircle,
        className: 'bg-red-500/20 text-red-400 border-red-500/50'
      },
      canceled: {
        label: 'Annulé',
        icon: XCircle,
        className: 'bg-gray-500/20 text-gray-400 border-gray-500/50'
      },
      incomplete: {
        label: 'Incomplet',
        icon: AlertCircle,
        className: 'bg-orange-500/20 text-orange-400 border-orange-500/50'
      }
    };

    const config = statusConfig[status] || statusConfig.active;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${config.className}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const formatDate = (isoDate) => {
    return new Date(isoDate).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-gold" />
        <p className="text-gray-400">Chargement des abonnements...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-playfair font-bold text-white">
            Abonnements Actifs
          </h2>
          <p className="text-gray-400 mt-1">
            {subscriptions.length} abonnement{subscriptions.length > 1 ? 's' : ''} récurrent{subscriptions.length > 1 ? 's' : ''}
          </p>
        </div>
        <Button
          onClick={fetchSubscriptions}
          variant="outline"
          className="border-gold/30 text-gold hover:bg-gold/10"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Subscriptions List */}
      {subscriptions.length === 0 ? (
        <Card className="bg-light/5 border-gold/20">
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400">Aucun abonnement actif pour le moment</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {subscriptions.map((sub) => (
            <Card 
              key={sub.id} 
              className={`bg-light/5 border-gold/20 hover:border-gold/40 transition-colors ${
                sub.status === 'past_due' ? 'border-red-500/30' : ''
              }`}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-white text-xl font-playfair mb-2">
                      {sub.plan_name}
                    </CardTitle>
                    <div className="space-y-1">
                      <p className="text-sm text-gray-400">
                        <span className="font-semibold text-white">{sub.customer_name}</span>
                      </p>
                      <p className="text-sm text-gray-400">{sub.customer_email}</p>
                    </div>
                  </div>
                  <div className="ml-4">
                    {getStatusBadge(sub.status, sub.cancel_at_period_end)}
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Pricing */}
                <div className="bg-gradient-to-r from-gold/20 to-transparent p-4 rounded border border-gold/20">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-gold">
                      {(sub.amount / 100).toFixed(2)}
                    </span>
                    <Euro className="w-5 h-5 text-gold" />
                    <span className="text-gray-400">/ {sub.interval === 'month' ? 'mois' : 'an'}</span>
                  </div>
                </div>

                {/* Period Info */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Période actuelle :</span>
                    <span className="text-white">{formatDate(sub.current_period_start)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Prochaine facturation :</span>
                    <span className="text-gold font-semibold">
                      {formatDate(sub.current_period_end)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Créé le :</span>
                    <span className="text-white">{formatDate(sub.created_at)}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="pt-4 space-y-2">
                  {sub.cancel_at_period_end ? (
                    <Button
                      onClick={() => handleReactivate(sub.id)}
                      disabled={actionLoading === sub.id}
                      className="w-full bg-neon hover:bg-neon/80 text-dark"
                    >
                      {actionLoading === sub.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Réactiver l'abonnement
                        </>
                      )}
                    </Button>
                  ) : sub.status === 'active' || sub.status === 'trialing' ? (
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        onClick={() => handleCancel(sub.id, false)}
                        disabled={actionLoading === sub.id}
                        variant="outline"
                        className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                      >
                        {actionLoading === sub.id ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Clock className="w-4 h-4 mr-2" />
                            Annuler (fin période)
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleCancel(sub.id, true)}
                        disabled={actionLoading === sub.id}
                        variant="outline"
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        {actionLoading === sub.id ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <XCircle className="w-4 h-4 mr-2" />
                            Annuler maintenant
                          </>
                        )}
                      </Button>
                    </div>
                  ) : null}

                  {sub.status === 'past_due' && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded p-3 text-sm">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                        <div className="text-red-300">
                          <p className="font-semibold">Paiement échoué</p>
                          <p className="text-xs mt-1">Le client doit mettre à jour son moyen de paiement</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
